import { useCallback, useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import {
  ILoyaltyBadgesQuery,
  ILoyaltyServiceBadgesQuery,
  useLoyaltyBadgesQuery,
  useLoyaltyServiceBadgesQuery,
} from 'generated/graphql-gateway';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { maybeLocaleRaw } from 'utils/graphql/parsers';

import { ILoyaltyBadge } from './types';
import { getBadgeProgressPercentage } from './utils';

type LegacyBadge = NonNullable<NonNullable<ILoyaltyBadgesQuery['loyaltyUserV2']>['badges']>[number];
type ServiceBadge = NonNullable<ILoyaltyServiceBadgesQuery['loyaltyServiceBadges']>[number];

const mapLegacyBadge = (badge: LegacyBadge, language: LANGUAGES): ILoyaltyBadge => ({
  id: badge.id,
  earnedAt: badge.earnedAt,
  name: badge.name?.[language] ?? '',
  description: badge.description?.[language],
  imageId: badge.image?.[language]?.imageUrl,
  imageAlt: badge.image?.[language]?.imageAlt,
  lottie: badge.lottie,
  shortDescription: badge.shortDescription?.[language],
  terms: badge.terms?.[language],
  lockedCtaLink: badge.ctaLink?.[language],
  lockedCtaText: badge.ctaText?.[language],
  progress: badge.progress
    ? {
        currentValue: badge.progress.currentValue,
        targetValue: badge.progress.targetValue,
        itemLabel: badge.progress.itemLabel?.[language],
        encouragingPhrase: badge.progress.encouragingPhrase?.[language],
      }
    : undefined,
});

const mapServiceBadge = (badge: ServiceBadge, language: LANGUAGES): ILoyaltyBadge => ({
  id: badge.id,
  earnedAt: badge.earnedAt,
  name: badge.name?.[language] ?? '',
  description: maybeLocaleRaw(badge.description),
  imageId: badge.image?.[language]?.imageUrl,
  imageAlt: badge.image?.[language]?.imageAlt,
  lottie: badge.lottie,
  shortDescription: maybeLocaleRaw(badge.shortDescription),
  terms: maybeLocaleRaw(badge.terms),
  lockedCtaLink: badge.ctaLink?.[language],
  lockedCtaText: badge.ctaText?.[language],
  progress: badge.progress
    ? {
        currentValue: badge.progress.currentValue,
        targetValue: badge.progress.targetValue,
        itemLabel: badge.progress.itemLabel?.[language],
        encouragingPhrase: badge.progress.encouragingPhrase?.[language],
      }
    : undefined,
});

const sortBadges = (badges: ILoyaltyBadge[]): ILoyaltyBadge[] =>
  badges.sort((a, b) => {
    if (a.earnedAt && !b.earnedAt) {
      return -1;
    }
    if (!a.earnedAt && b.earnedAt) {
      return 1;
    }
    if (a.earnedAt && b.earnedAt) {
      return new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime();
    }
    const aPercentage = a.progress ? getBadgeProgressPercentage(a.progress) : 0;
    const bPercentage = b.progress ? getBadgeProgressPercentage(b.progress) : 0;
    if (bPercentage !== aPercentage) {
      return bPercentage - aPercentage;
    }
    return (b.progress?.currentValue ?? 0) - (a.progress?.currentValue ?? 0);
  });

export const useGetLoyaltyBadges = ({
  skip = false,
  fetchPolicy = 'cache-and-network',
}: { skip?: boolean; fetchPolicy?: WatchQueryFetchPolicy } = {}) => {
  const { language } = useLocale();
  const { loyaltyUserId, loading: authLoading } = useLoyaltyUserId();
  const enableLoyaltyServiceBadges = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_BADGES)
  );
  const shouldSkip = skip || !loyaltyUserId;
  const legacyQuery = useLoyaltyBadgesQuery({
    fetchPolicy,
    variables: { loyaltyId: loyaltyUserId, where: { fetchAll: true } },
    skip: shouldSkip || enableLoyaltyServiceBadges,
  });
  const serviceQuery = useLoyaltyServiceBadgesQuery({
    fetchPolicy,
    variables: { input: { fetchAll: true } },
    skip: shouldSkip || !enableLoyaltyServiceBadges,
  });

  const legacyBadges = legacyQuery.data?.loyaltyUserV2?.badges;
  const serviceBadges = serviceQuery.data?.loyaltyServiceBadges;
  const activeBadges = enableLoyaltyServiceBadges ? serviceBadges : legacyBadges;
  const activeQuery = enableLoyaltyServiceBadges ? serviceQuery : legacyQuery;

  // With fetchPolicy 'cache-and-network', Apollo sets loading=true during background
  // refetches even when cached data is already available. Use initialLoading to gate
  // skeletons/loaders so cached content is shown immediately on warm-cache mounts.

  // While auth is still resolving, loyaltyUserId is missing and the query is skipped.
  // Then Apollo sends loading: false
  const loyaltyUserIdLoading = !loyaltyUserId && authLoading;
  const initialLoading = loyaltyUserIdLoading || (activeQuery.loading && !activeBadges);

  const badges: ILoyaltyBadge[] = useMemo(() => {
    const mapped = enableLoyaltyServiceBadges
      ? (serviceBadges ?? []).map(badge => mapServiceBadge(badge, language))
      : (legacyBadges ?? []).map(badge => mapLegacyBadge(badge, language));

    // Earned badges appear first, sorted by earnedAt descending (most recently earned first).
    // Unearned badges follow, sorted by normalized progress percentage descending (closest to
    // completion first), then by raw currentValue as a tie-breaker, then by original server order.
    // This ensures a stable display order regardless of fetch policy or server response ordering.
    return sortBadges(mapped);
  }, [enableLoyaltyServiceBadges, language, legacyBadges, serviceBadges]);

  const { refetch: refetchLegacyBadges } = legacyQuery;
  const { refetch: refetchServiceBadges } = serviceQuery;
  const refetch = useCallback(() => {
    if (shouldSkip) {
      return Promise.resolve();
    }

    if (enableLoyaltyServiceBadges) {
      return refetchServiceBadges({ input: { fetchAll: true } });
    }

    return refetchLegacyBadges({ loyaltyId: loyaltyUserId, where: { fetchAll: true } });
  }, [
    enableLoyaltyServiceBadges,
    loyaltyUserId,
    refetchLegacyBadges,
    refetchServiceBadges,
    shouldSkip,
  ]);

  return {
    badges,
    error: activeQuery.error,
    initialLoading,
    loading: activeQuery.loading,
    refetch,
  };
};
